import { useTranslation } from "@i18n";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import useFileDelete from "./useFileDelete";

interface FileDeleteProps {
  /** The rows to delete; forwarded straight to `useFileDelete`, which owns their shape. */
  /** Callers pass `null` when nothing is selected, and rows carrying their own extra members. */
  selectedFiles?: Parameters<typeof useFileDelete>[0]["selectedFiles"] | null;
  onCloseModal?: () => void;
  path?: string | null;
  onDeleteSuccess?: Parameters<typeof useFileDelete>[0]["onDeleteSuccess"];
  workspaceId?: string;
  isFileUpload?: boolean;
  /**
   * METHOD-style optional callback: callers supply handlers with differing payloads, so this is
   * declared to accept whatever `useFileDelete` hands it.
   */
  onDeleteFiles?: Parameters<typeof useFileDelete>[0]["onDeleteFiles"];
  requestDirectoryTotal?: boolean;
  directoryTotalProjectId?: string;
}

function FileDelete({
  selectedFiles = [],
  onCloseModal,
  path,
  onDeleteSuccess,
  workspaceId,
  isFileUpload = false,
  onDeleteFiles,
  requestDirectoryTotal,
  directoryTotalProjectId,
}: Readonly<FileDeleteProps>) {
  const { t } = useTranslation();

  // Use the centralized hook for all business logic
  const {
    showConstituentWarning,
    showDeleteWarning,
    deleteAssociatedTasks,
    setDeleteAssociatedTasks,
    deleteWarningText,
    selectedResourceLabel,
    isSchedule,
    handleDeleteFiles,
    handleCancelDelete,
    getConstituentFilesWarningData,
  } = useFileDelete({
    // Callers pass `null` for an empty selection and `string | null` for `path`; the hook declares
    // both non-null. Erased assertions at the single boundary rather than widening the hook.
    selectedFiles: selectedFiles!,
    workspaceId,
    path: path!,
    isFileUpload,
    onDeleteSuccess,
    onDeleteFiles,
    onCloseModal,
    requestDirectoryTotal,
    directoryTotalProjectId,
  });

  const footerContent = () => (
    <div className="flex items-center gap-6">
      <ButtonComponent
        onClick={handleCancelDelete}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        data-testid="cancelDeleteFileFolderBtn"
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
      <ButtonComponent
        onClick={handleDeleteFiles as typeof handleDeleteFiles & React.MouseEventHandler<HTMLButtonElement>}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        data-testid="confirmDeleteFileFolderBtn"
      >
        <Text>{t("Confirm")}</Text>
      </ButtonComponent>
    </div>
  );

  const modelContent = () => (
    <div>
      {showDeleteWarning && <div data-testid="deleteFileFolderWarningText">{deleteWarningText}</div>}
      {showConstituentWarning && (
        <div className="flex flex-col gap-4">
          <div>
            <Text>{t("Below files are the constituent files of")}</Text>
          </div>
          <div>
            {getConstituentFilesWarningData().map((item) => (
              <div key={item.fileId} className="flex">
                <div>{item.fileName}</div>
                <div className="ps-4 font-semibold">{item.parentFileNames}</div>
              </div>
            ))}
          </div>
          <div>
            <Text>{t("Are you sure you want to delete?")}</Text>
          </div>
        </div>
      )}
      {isSchedule && (
        <span>
          <CheckBoxComponent
            label="Delete associated tasks"
            id="deleteTask"
            checked={deleteAssociatedTasks}
            onChange={(args) => {
              setDeleteAssociatedTasks(args.target.checked);
            }}
          />{" "}
        </span>
      )}
    </div>
  );

  return (
    <ModalStateDialogComponent
      onCancel={handleCancelDelete}
      headerTitle={t("Delete {{label}}", { label: selectedResourceLabel })}
      footerContent={footerContent()}
      modelContent={modelContent()}
      width="50%"
      isVisible
    />
  );
}

export default FileDelete;
